import { useEffect, useState } from 'react';
import { fetchCampaigns } from '../api/client.js';
import type { CampaignRow, CampaignsResponse, CampaignVariantRow } from '../api/client.js';
import { Stamp } from '../components/Stamp.js';
import { StateChip } from '../components/StateChip.js';
import { stateDictionaryKey, useDictionary } from '../i18n/context.js';
import type { Dictionary } from '../i18n/types.js';

// ---------------------------------------------------------------------------
// Campaigns list (UI structure spec 10.1): campaign name, project,
// objective, present platform variants, per-variant state summary, and next
// proposed or scheduled publication. Filters (10.1: project, platform
// coverage, aggregate status, updated date) are deferred to a later task --
// this screen gets the list itself correct first.
//
// "Create campaign" (10.3) opens the campaign editor (section 11), which is
// out of this plan's scope and has no screen yet -- unlike Create project,
// there is no existing plain-form substitute for it (contrast
// CreateProjectForm.tsx, reused on projects.tsx), so the action is omitted
// here rather than rendered as a control that goes nowhere. "Open campaign"
// is omitted for the same reason: no campaign detail route exists yet for
// it to open.
//
// The rule this screen exists to prove out is 10.1's own: "An aggregate
// campaign label may summarize mixed outcomes but never replaces
// per-variant states." `campaignSummary`/`campaignSummaryText` produce the
// summary line; every variant's own `StateChip` is rendered alongside it,
// never instead of it.
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: CampaignsResponse };

// Duplicated from home.tsx/projects.tsx deliberately -- route files do not
// share UI helpers in this codebase (home.tsx's own comment on this exact
// ternary).
const platformLabel = (platform: 'x' | 'pinterest'): string => (platform === 'pinterest' ? 'Pinterest' : 'X');

export type CampaignSummary =
  | { kind: 'no_variants' }
  | { kind: 'review_required'; platforms: Array<'x' | 'pinterest'> }
  | { kind: 'uniform'; state: string; count: number; total: number }
  | { kind: 'mixed'; parts: Array<{ state: string; count: number }> };

// The pure classifier behind the summary line -- exported and tested
// directly (the way `connectionView` in project.tsx is), because the
// decision itself, not the markup around it, is what §10.1's rule is about.
//
// `needs_review` takes priority over every other grouping: a campaign that
// is otherwise uniform or only-just-mixed still needs a human to look at
// the platform(s) sitting in review, and that is the one fact this summary
// cannot bury under a count.
export const campaignSummary = (variants: CampaignVariantRow[]): CampaignSummary => {
  if (variants.length === 0) return { kind: 'no_variants' };

  const reviewPlatforms = variants.filter((v) => v.state === 'needs_review').map((v) => v.platform);
  if (reviewPlatforms.length > 0) return { kind: 'review_required', platforms: reviewPlatforms };

  const states = variants.map((v) => v.state);
  if (states.every((s) => s === states[0])) {
    return { kind: 'uniform', state: states[0] as string, count: variants.length, total: variants.length };
  }

  // Grouped in order of first appearance, not alphabetically or by some
  // fixed state ranking -- there is no canonical order for "how it turned
  // out" the way there is for "what still needs a decision" above.
  const order: string[] = [];
  const counts = new Map<string, number>();
  for (const s of states) {
    if (!counts.has(s)) order.push(s);
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  return { kind: 'mixed', parts: order.map((state) => ({ state, count: counts.get(state) ?? 0 })) };
};

export const campaignSummaryText = (dict: Dictionary, summary: CampaignSummary): string => {
  const stateLabel = (state: string): string => dict.ui.campaigns.summaryStateLabel[stateDictionaryKey(state)];

  switch (summary.kind) {
    case 'no_variants':
      return dict.ui.campaigns.summaryNoVariants;
    case 'review_required':
      return dict.ui.campaigns.summaryReviewRequired(summary.platforms.map(platformLabel).join(dict.ui.campaigns.summaryJoin));
    case 'uniform':
      return dict.ui.campaigns.summaryUniform(summary.count, summary.total, stateLabel(summary.state));
    case 'mixed':
      return summary.parts
        .map((part) => dict.ui.campaigns.summaryPart(part.count, stateLabel(part.state)))
        .join(dict.ui.campaigns.summaryJoin);
    default: {
      const exhaustive: never = summary;
      return exhaustive;
    }
  }
};

const CampaignRowView = ({ campaign }: { campaign: CampaignRow }): React.JSX.Element => {
  const dict = useDictionary();
  const summary = campaignSummary(campaign.variants);

  return (
    <tr data-testid={`campaign-row-${campaign.id}`}>
      <td>
        <div className="stack-1">
          <span className="strong">{campaign.name}</span>
          <span className="t-meta">{campaign.projectName}</span>
        </div>
      </td>
      <td><span className="t-meta">{campaign.objective ?? dict.ui.campaigns.noObjective}</span></td>
      <td>
        <div className="stack-1">
          <span className="t-body">{campaignSummaryText(dict, summary)}</span>
          {campaign.variants.length > 0 && (
            <div className="row wrap" style={{ gap: 'var(--sp-2)' }}>
              {campaign.variants.map((v) => (
                <span key={v.id} className="row" style={{ gap: 'var(--sp-1)' }}>
                  <span className="t-meta">{platformLabel(v.platform)}</span>
                  <StateChip state={v.state} />
                </span>
              ))}
            </div>
          )}
        </div>
      </td>
      <td>
        {campaign.nextPublishAtUtc
          ? <Stamp instant={campaign.nextPublishAtUtc} compact />
          : <span className="t-meta">{dict.ui.campaigns.noUpcoming}</span>}
      </td>
    </tr>
  );
};

export const CampaignsRoute = (): React.JSX.Element => {
  const dict = useDictionary();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = (): void => {
    setState({ status: 'loading' });
    fetchCampaigns()
      .then((data) => setState({ status: 'ready', data }))
      .catch(() => setState({ status: 'error' }));
  };

  useEffect(load, []);

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <div className="between">
        <h1 className="t-title">{dict.ui.campaigns.title}</h1>
      </div>

      {state.status === 'loading' && <p className="t-meta">{dict.ui.campaigns.loading}</p>}

      {state.status === 'error' && (
        <div className="alert alert--danger">
          <div className="stack-1">
            <div className="alert__title">{dict.ui.campaigns.loadFailed}</div>
            <button type="button" className="btn" onClick={load}>{dict.ui.campaigns.retry}</button>
          </div>
        </div>
      )}

      {state.status === 'ready' && (
        <div className="panel">
          <div className="panel__body panel__body--flush">
            {state.data.campaigns.length === 0
              ? (
                <div className="empty">
                  <div className="empty__title">{dict.ui.campaigns.noCampaigns}</div>
                  <div className="empty__body">{dict.ui.campaigns.noCampaignsBody}</div>
                </div>
              )
              : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>{dict.ui.campaigns.nameColumn}</th>
                      <th>{dict.ui.campaigns.objectiveColumn}</th>
                      <th>{dict.ui.campaigns.variantsColumn}</th>
                      <th>{dict.ui.campaigns.nextPublishColumn}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.campaigns.map((campaign) => <CampaignRowView key={campaign.id} campaign={campaign} />)}
                  </tbody>
                </table>
              )}
          </div>
        </div>
      )}
    </div>
  );
};
